import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Toast } from "@tento-ui/components/toast/toast";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { CalendarProviders } from "../calendar-providers";
import { OAuthPage } from "../oauth.page";
import { IntegrationsSelector } from "../sections/integrations-selector";

@component({
  name: "std-expired-auth-card",
})
export class ExpiredAuthCard extends LonaWebComponent {
  private source: string = "";
  private authId: string = "";

  private $message: HTMLParagraphElement = this.$("message");
  private $reauthButton: HTMLButtonElement = this.$("reauth-button");

  static makeWith(props: ExpiredAuthCard.Props): ExpiredAuthCard {
    return ExpiredAuthCard.make().bind(props);
  }

  bind(props: ExpiredAuthCard.Props): ExpiredAuthCard {
    // Cleanup previous GESTURE_MANAGER handler before rebinding
    GESTURE_MANAGER.unregister(this.$reauthButton);

    this.source = props.source;
    this.authId = props.authId;

    this.$message.textContent =
      "Authentication expired. Please re-authenticate to continue syncing.";

    GESTURE_MANAGER.addPointerEvent(this.$reauthButton, {
      onClick: async () => {
        await this.handleReauth();
      },
    });

    return this;
  }

  private async handleReauth(): Promise<void> {
    // Calendar providers reauth through the registry descriptor; the
    // non-calendar integrations keep their bespoke flows below.
    const calendarProvider = CalendarProviders.get(this.source);
    if (calendarProvider) {
      await calendarProvider.reauth();
      DomUtils.removeFromDom(this);
      return;
    }
    switch (this.source) {
      case "whoop":
        this.reauthWhoop();
        break;
      case "garmin":
        this.reauthGarmin();
        break;
      default:
        Toast.error(`Unknown integration: ${this.source}`);
    }
  }

  private reauthWhoop(): void {
    IntegrationsSelector.openWhoopAuthPopup(() => {
      Toast.info("Successfully re-authenticated with Whoop");
      DomUtils.removeFromDom(this);
    });
  }

  private reauthGarmin(): void {
    OAuthPage.show({
      integration: "Garmin",
      title: "Re-authenticate Garmin",
      imgUrl: "https://lona.so/@/static/img/integrations/garmin.svg",
      onSuccess: () => {
        DomUtils.removeFromDom(this);
      },
    });
  }


  static $styles: Style.Compat[] = [
    Button.$style,
    css`
      :host {
        display: block;
        margin-left: 52px;
        margin-block: 8px;
        max-width: 480px;
      }

      #container {
        background: var(--bg-surface-light);
        border: 1px solid var(--divider);
        border-radius: 8px;
        padding: 12px;
        gap: 8px;
      }

      #message {
        color: var(--text-sec);
        margin: 0;
      }

      #reauth-button {
        align-self: flex-start;
      }
    `,
  ];

  static $html = html`
    <std-col id="container">
      <p id="message" small></p>
      <std-row>
        <std-spacer></std-spacer>
        <button id="reauth-button" primary>Re-authenticate</button>
      </std-row>
    </std-col>
  `;
}

export namespace ExpiredAuthCard {
  export interface Props {
    source: string;
    authId: string;
  }
}
